import React, { useState, useEffect, useRef } from 'react'
import './FilterBar.scss'
import FilterBarSelect from './FilterBarSelect'

export function FilterBar({ fields, onChange, placeholder = 'Add filter...' }) {
  const [filters, setFilters] = useState([])
  const [adding, setAdding] = useState(false)
  const [draftField, setDraftField] = useState('')
  const [draftValue, setDraftValue] = useState('')
  const draftValueInputRef = useRef(null)

  // Serializes filters as "field:value,field2:value2"
  function encodeFilters(filters) {
    return filters.map(f => `${encodeURIComponent(f.field)}:${encodeURIComponent(f.value)}`).join(',')
  }
  // Deserializes filters from the format "field:value,field2:value2"
  function decodeFilters(q) {
    if (!q) return []
    return q.split(',')
      .map(pair => {
        const [field, ...valueParts] = pair.split(':')
        if (!field || valueParts.length === 0) return null
        return {
          id: crypto.randomUUID(),
          field: decodeURIComponent(field),
          value: decodeURIComponent(valueParts.join(':'))
        }
      })
      .filter(Boolean)
  }

  // Updates the URL with the filters
  function updateUrl(filters) {
    const q = encodeFilters(filters)
    const url = new URL(window.location.href)
    if (filters.length > 0) {
      url.searchParams.set('q', q)
    } else {
      url.searchParams.delete('q')
    }
    window.history.replaceState({}, '', url.toString())
  }

  // Loads filters from the URL on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const q = params.get('q')
    if (q) {
      setFilters(decodeFilters(q))
    }
  }, [])

  const finalizeAdd = (field, value) => {
    if (!field || !value) return
    setFilters(prev => {
      const newFilters = [...prev, { id: crypto.randomUUID(), field, value }]
      updateUrl(newFilters)
      // onChange removido daqui para evitar setState no pai durante render
      return newFilters
    })
    setAdding(false)
    setDraftField('')
    setDraftValue('')
  }

  const removeFilter = id => {
    setFilters(prev => {
      const newFilters = prev.filter(f => f.id !== id)
      updateUrl(newFilters)
      if (newFilters.length === 0) { setAdding(false); setDraftField(''); setDraftValue('') }
      return newFilters
    })
  }

  const clearAll = e => {
    e?.stopPropagation()
    setFilters([])
    setAdding(false)
    setDraftField('')
    setDraftValue('')
    updateUrl([])
  }

  // Dispara onChange quando a lista de filtros muda (forma segura)
  useEffect(() => {
    if (onChange) onChange(filters)
  }, [filters, onChange])

  const availableFields = fields.filter(f => !filters.some(fl => fl.field === f.key) || f.key === draftField)
  const selectedFieldDef = fields.find(f => f.key === draftField)
  const hasPredefinedValues = selectedFieldDef && Array.isArray(selectedFieldDef.values)

  useEffect(() => { if (adding && draftField && !hasPredefinedValues && draftValueInputRef.current) { draftValueInputRef.current.focus(); draftValueInputRef.current.select() } }, [adding, draftField, hasPredefinedValues])

  return (
    <div
      className='filter-bar'
      onClick={() => {
        if (!adding && availableFields.filter(f => !filters.some(fl => fl.field === f.key)).length > 0) {
          setAdding(true)
          setDraftField('')
          setDraftValue('')
        }
      }}
    >
      <div className='filter-bar__content'>
        {filters.map(f => (
          <span key={f.id} className='filter-bar__chip'>
            {f.field} = {f.value}
            <button
              type='button'
              className='filter-bar__remove-btn'
              aria-label='Remover filtro'
              onClick={e => { e.stopPropagation(); removeFilter(f.id) }}
            >×</button>
          </span>
        ))}

        {!adding && filters.length === 0 && (
          <span className='filter-bar__placeholder'>{placeholder}</span>
        )}
        {adding && !draftField && availableFields.filter(f => !filters.some(fl => fl.field === f.key)).length > 0 && (
          <FilterBarSelect
            options={availableFields.filter(f => !filters.some(fl => fl.field === f.key)).map(f => ({ value: String(f.key), label: String(f.label) }))}
            value={draftField}
            onChange={val => setDraftField(val)}
            placeholder='Select field'
            autoOpen={true}
            className='filter-bar__select'
            enableSearch={false}
          />
        )}
        {adding && draftField && hasPredefinedValues && (
          <>
            <span className='filter-bar__draft-label'>{selectedFieldDef?.label}:</span>
            <FilterBarSelect
              options={selectedFieldDef.values.map(v => ({ value: String(v), label: String(v) }))}
              value={draftValue}
              onChange={val => { setDraftValue(val); if (val) finalizeAdd(draftField, val) }}
              placeholder='Value'
              autoOpen={true}
              className='filter-bar__select'
              onClose={() => {
                if (!draftValue) {
                  setDraftField('')
                  setDraftValue('')
                  setAdding(false)
                }
              }}
            />
          </>
        )}
        {adding && draftField && !hasPredefinedValues && (
          <>
            <span className='filter-bar__draft-label'>{selectedFieldDef?.label}:</span>
            <input
              ref={draftValueInputRef}
              className='filter-bar__input'
              placeholder='Value'
              value={draftValue}
              onChange={e => setDraftValue(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && draftValue) finalizeAdd(draftField, draftValue) }}
              onBlur={() => finalizeAdd(draftField, draftValue)}
            />
          </>
        )}
      </div>
      {filters.length > 0 && (
        <button
          type='button'
          className='filter-bar__clear-btn'
          onClick={e => { e.stopPropagation(); clearAll() }}
          aria-label='Limpar filtros'
        >
          Clear
        </button>
      )}
    </div>
  )
}
